import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import React, { FC, useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import BaseDataTable from "../../../../../components/modules/BaseDataTable";
import CheckBox from "../../../../../components/elements/CheckBox";
import CreativeAssets from "../CreativeAssets";
import api from "../../../../../api";
import classes from "./AdGroupsDashboard.module.scss";
import { AdGroupTemplate, CreativeTemplateAsset } from "../../../../../types/adGroupTypes";
import { AdvertiserType } from "../../../../../types/advertiserTypes";
import {
  CreativeAssetFromTemplate,
  CreativeAssetToCreate,
  SelectedAdGroupTemplate,
} from "../../../../../types/campaignTypes";
import { PageStepComponentProps } from "../../../../../components/modules/BaseStepper/BaseStepper";
import { errorToaster } from "../../../../../helpers/toasters";
import { setAdGroupToCreate } from "../../../../../redux/reducers/campaignToCreate";
import { truncateText } from "../../../../../helpers/truncateText";

type AdGroupsCreativesMap = Record<string, CreativeTemplateAsset[]>;

type AdgroupsDashboardProps = PageStepComponentProps & {
  selectedAdvertiser: AdvertiserType;
  adGroupTemplates: AdGroupTemplate[];
};

const fetchAdGroupCreatives = async (adGroupTemplateId: string) => {
  try {
    const adGroupCreatives = await api.getCreativesTemplatesByAdGroup(adGroupTemplateId);
    return adGroupCreatives;
  } catch (error) {
    const err = error as any;
    throw new Error(err.message);
  }
};

const AdGroupsDashboard: FC<AdgroupsDashboardProps & { showCheckbox?: boolean }> = ({
  selectedAdvertiser,
  adGroupTemplates,
  isAttemptingToGoNext,
  setIsAttemptingToGoNext,
  handleNext,
  showCheckbox,
}) => {
  const [selectedAdGroup, setSelectedAdGroup] = useState<SelectedAdGroupTemplate | null>(null);
  const [adGroupsCreativesMap, setAdGroupsCreativesMap] = useState<AdGroupsCreativesMap>({});
  const [isFetchingCreatives, setIsFetchingCreatives] = useState<boolean>(false);

  const dispatch = useDispatch();

  const columns = [
    ...(showCheckbox ? [{ key: "selected", label: "Select", minWidth: 50 }] : []),
    { key: "name", label: "Name", minWidth: 120 },
    { key: "createdBy", label: "Created By", minWidth: 120 },
    { key: "description", label: "Description", minWidth: 170 },
    { key: "createdDate", label: "Created Date", minWidth: 100 },
  ] as const;

  useEffect(() => {
    if (isAttemptingToGoNext && handleNext && setIsAttemptingToGoNext) {
      if (selectedAdGroup) {
        dispatch(
          setAdGroupToCreate({
            name: selectedAdGroup.adGroup.name,
            description: selectedAdGroup.adGroup.description,
            adGroupType: selectedAdGroup.adGroup.adGroupType,
            creativeAssets: selectedAdGroup.creatives,
          }),
        );
        handleNext();
        setIsAttemptingToGoNext(false);
      } else {
        errorToaster("Please, select Ad Group to continue.", 5000);
        setIsAttemptingToGoNext(false);
      }
    }
  }, [dispatch, handleNext, isAttemptingToGoNext, selectedAdGroup, setIsAttemptingToGoNext]);

  useEffect(() => {
    if (selectedAdGroup && showCheckbox) {
      dispatch(
        setAdGroupToCreate({
          name: selectedAdGroup.adGroup.name,
          description: selectedAdGroup.adGroup.description,
          adGroupType: selectedAdGroup.adGroup.adGroupType,
          creativeAssets: selectedAdGroup.creatives,
        }),
      );
    }
  }, [dispatch, selectedAdGroup, showCheckbox]);

  const handleAdGroupExpand = async (adGroupTemplateId: string): Promise<CreativeTemplateAsset[] | null> => {
    try {
      const adGroupCreatives = adGroupsCreativesMap[adGroupTemplateId];
      if (!adGroupCreatives) {
        setIsFetchingCreatives(true);
        const { payload } = await fetchAdGroupCreatives(adGroupTemplateId);
        setAdGroupsCreativesMap({
          ...adGroupsCreativesMap,
          [adGroupTemplateId]: payload,
        });
        setIsFetchingCreatives(false);
        return payload;
      }
      return adGroupCreatives;
    } catch (error) {
      const err = error as any;
      console.log("error at handleAdGroupCreativesLoad click: ", err.message);
      setIsFetchingCreatives(false);
      return null;
    }
  };

  const handleChangeSelectedAdGroup = (adGroupTemplateId: string, selected: boolean) => async () => {
    const creatives = await handleAdGroupExpand(adGroupTemplateId);
    const adGroupToSet = adGroupTemplates.find((adGroup) => adGroup.id === adGroupTemplateId) as AdGroupTemplate;

    if (selected) {
      setSelectedAdGroup(null);
    } else if (creatives) {
      const creativeAssetsToCreate: CreativeAssetToCreate[] = creatives.map((creative) => {
        const fileType = creative.creativeType.split("/")[0];

        const body: CreativeAssetFromTemplate = {
          sourceUrl: creative.creativeTemplateSrc,
          height: creative.height,
          width: creative.width,
          fileType: fileType as "image" | "video",
          name: creative.name,
          size: creative.size_in_bytes,
          duration: creative.duration ? creative.duration : undefined,
          mimetype: creative.creativeType,
        };

        return body;
      });

      setSelectedAdGroup({
        adGroup: adGroupToSet,
        creatives: creativeAssetsToCreate,
      });
    }
  };

  const rows = adGroupTemplates
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))
    .map(({ name, createdBy, description, createdAt, id }) => ({
      id,
      selected: (() => {
        const selected = selectedAdGroup?.adGroup.id === id;
        return <CheckBox label="" checked={selected} onChange={handleChangeSelectedAdGroup(id, selected)} />;
      })(),
      name: (() => (
        <Tooltip title={name}>
          <Typography>{truncateText(name, 36)}</Typography>
        </Tooltip>
      ))(),
      createdBy: (() => (
        <Tooltip title={createdBy}>
          <Typography>{truncateText(createdBy, 36)}</Typography>
        </Tooltip>
      ))(),
      description: (() => (
        <Tooltip title={description}>
          <Typography>{truncateText(description, 36)}</Typography>
        </Tooltip>
      ))(),
      createdDate: new Date(createdAt).toLocaleDateString(),
      collapsableConfigs: {
        collapsable: true,
        collapsableChild: (
          <CreativeAssets isFetchingCreatives={isFetchingCreatives} creativeAssets={adGroupsCreativesMap[id] ?? []} />
        ),
        expandControlHandler: () => {
          handleAdGroupExpand(id);
        },
      },
    }));

  return (
    <div className={classes.dashboardContainer}>
      <div className={classes.dashboardControls}>
        <Typography>
          These are the current ad groups associated with <span>{selectedAdvertiser.name}</span>{" "}
        </Typography>
      </div>

      <BaseDataTable columns={columns} rows={rows} />
    </div>
  );
};

export default AdGroupsDashboard;
